/**
 * ModalFrame — the frame the dashboard's multi-control modals share: a fixed header with
 * the title and a 44px close button, a body that scrolls, and a fixed footer that holds
 * the actions.
 *
 * Single-pane shows the frame full screen and Split shows it as a centred card. Both are
 * the same Modal with different props, so an open modal keeps its state when a phone
 * rotates across the size classes.
 */

import { ActionIcon, Box, Group, Modal, SimpleGrid } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useSizeClass } from '@/hooks/useSizeClass';
import { useVisualViewport } from '@/hooks/useVisualViewport';
import type { ReactNode } from 'react';

const CARD_VIEWPORT_SHARE = 0.85;
const CARD_MAX_HEIGHT = 720;
const BORDER = '1px solid var(--mantine-color-gray-3)';

interface Props {
  opened: boolean;
  onClose: () => void;
  title: string;
  /** The width of the Split card. */
  size?: 'md' | 'lg';
  closeOnClickOutside?: boolean;
  /** Cancel, then the main action. A read-only modal leaves this out and gets no footer. */
  actions?: ReactNode;
  children: ReactNode;
}

export function ModalFrame({
  opened,
  onClose,
  title,
  size = 'md',
  closeOnClickOutside,
  actions,
  children,
}: Props) {
  const { sizeClass, shortHeight } = useSizeClass();
  const viewport = useVisualViewport();
  const fullScreen = sizeClass === 'single';

  // A modal portals outside the shell, so the full-screen frame clears the notch and the
  // home indicator itself. The Split card's margins already clear them.
  const inset = (edge: 'top' | 'right' | 'bottom' | 'left'): string =>
    fullScreen ? `env(safe-area-inset-${edge})` : '0px';
  // Short height tightens the frame's own spacing, which leaves the body more of a
  // phone's landscape height.
  const headerGap = shortHeight ? '4px' : 'var(--mantine-spacing-xs)';
  const bodyGap = shortHeight ? '6px' : 'var(--mantine-spacing-sm)';
  const sidePadding = {
    paddingLeft: `calc(var(--mantine-spacing-md) + ${inset('left')})`,
    paddingRight: `calc(var(--mantine-spacing-md) + ${inset('right')})`,
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={size}
      fullScreen={fullScreen}
      centered
      withCloseButton={false}
      padding={0}
      closeOnClickOutside={closeOnClickOutside}
      transitionProps={{ transition: fullScreen ? 'slide-up' : 'fade-down', duration: 200 }}
      styles={{
        // The layout viewport doesn't shrink for the on-screen keyboard on iOS, so the
        // modal takes the visual viewport's top and height. Its footer then sits on top
        // of the keyboard.
        inner: {
          top: viewport.offsetTop,
          bottom: 'auto',
          height: viewport.height,
          paddingTop: 0,
          paddingBottom: 0,
        },
        content: {
          display: 'flex',
          flexDirection: 'column',
          height: fullScreen
            ? '100%'
            : Math.min(Math.round(viewport.height * CARD_VIEWPORT_SHARE), CARD_MAX_HEIGHT),
          maxHeight: '100%',
          overflow: 'hidden',
        },
        body: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
      }}
    >
      <Group
        component="header"
        data-testid="modal-header"
        justify="space-between"
        wrap="nowrap"
        gap="xs"
        style={{
          flexShrink: 0,
          borderBottom: BORDER,
          paddingTop: `calc(${headerGap} + ${inset('top')})`,
          paddingBottom: headerGap,
          ...sidePadding,
        }}
      >
        {/* Modal.Title names the dialog for a screen reader. */}
        <Modal.Title
          fz="lg"
          fw={700}
          lh="md"
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Modal.Title>
        <ActionIcon variant="subtle" color="gray" aria-label="Close" onClick={onClose}>
          <IconX size={20} />
        </ActionIcon>
      </Group>

      <Box
        data-testid="modal-body"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          paddingTop: bodyGap,
          paddingBottom: bodyGap,
          ...sidePadding,
        }}
      >
        {children}
      </Box>

      {actions && (
        <SimpleGrid
          component="footer"
          data-testid="modal-footer"
          cols={2}
          spacing="xs"
          style={{
            flexShrink: 0,
            borderTop: BORDER,
            paddingTop: bodyGap,
            paddingBottom: `calc(${bodyGap} + ${inset('bottom')})`,
            ...sidePadding,
          }}
        >
          {actions}
        </SimpleGrid>
      )}
    </Modal>
  );
}
